import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "/assets/hero-home.jpg";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function Home() {
  const [wedding, setWedding] = useState(null);

  useEffect(() => {
    api.get("/wedding").then((r) => setWedding(r.data));
  }, []);

  if (!wedding) return <div className="p-10 text-center font-script text-3xl">Laden...</div>;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative max-w-5xl mx-auto px-6 pt-16 pb-24 text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]"
        >
          Un dono per due sposi ~ Een cadeau voor het bruidspaar
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="font-script text-7xl sm:text-8xl lg:text-9xl text-[#006A71] mt-6 leading-[0.9]"
        >
          {wedding.bride} <span className="text-[#006A71]">&</span> {wedding.groom}
        </motion.h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-serif text-2xl sm:text-3xl text-[#1F3E42] max-w-2xl mx-auto italic mt-4"
        >
          Amore per sempre
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="text-[#4A6165] max-w-xl mx-auto mt-6 leading-relaxed"
        >
          {wedding.intro_text}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
          className="mt-16 relative"
        >
          <img
            src={HERO_IMG}
            alt="Toscane"
            className="w-full max-h-[520px] object-cover rounded-sm brutal-shadow-lg"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="mt-12 flex gap-4 justify-center flex-wrap"
        >
          <NavLink to="/wensen" className="btn-primary" data-testid="hero-cta-wishes">
            Lees de wensen <ArrowRight className="w-3.5 h-3.5" />
          </NavLink>
          <NavLink to="/de-bende" className="btn-secondary" data-testid="hero-cta-bende">
            Ontmoet de bende
          </NavLink>
        </motion.div>
      </section>

      {/* DATES / TIMELINE */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Il nostro tempo insieme</p>
        <h2 className="font-script text-5xl sm:text-6xl text-[#006A71] mt-3">Jullie mijlpalen</h2>
        <div className="section-divider"><span className="section-divider-dot" /></div>

        <div className="grid sm:grid-cols-2 gap-10 mt-12 max-w-2xl mx-auto">
          <Milestone
            italian="Il matrimonio"
            title="Officieel getrouwd"
            value={fmtDate(wedding.wedding_date)}
          />
          <Milestone
            italian="La festa"
            title="Feest in Toscane"
            value={`${fmtDate(wedding.party_date)}${wedding.party_location ? ` · ${wedding.party_location}` : ""}`}
          />
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Il libro dei ricordi</p>
          <h2 className="font-script text-5xl sm:text-6xl text-[#006A71] mt-3">Blader rustig door</h2>
          <div className="section-divider"><span className="section-divider-dot" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
          <ChapterCard to="/de-bende" italian="La banda" title="De Bende" subtitle="Een woord per koppel" index={0} />
          <ChapterCard to="/wensen" italian="I desideri" title="Wensen" subtitle="Lieve woorden van vrienden" index={1} />
        </div>
      </section>
    </div>
  );
}

function Milestone({ italian, title, value }) {
  return (
    <div className="text-center">
      <p className="font-script text-3xl text-[#006A71]">{italian}</p>
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#1F3E42]/60 mt-2">{title}</p>
      <p className="font-serif text-xl text-[#1F3E42] mt-3">{value}</p>
    </div>
  );
}

function ChapterCard({ to, italian, title, subtitle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay: index * 0.08 }}
    >
      <NavLink to={to} data-testid={`chapter-card-${title}`} className="group block text-center">
        <div className="relative overflow-hidden mb-5 aspect-[3/4] bg-[#E8DFCF]">
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-script text-5xl text-[#006A71] group-hover:scale-110 transition-transform duration-500">
              {italian}
            </span>
          </div>
          <div className="absolute inset-0 border border-[#006A71]/20 m-3 pointer-events-none" />
        </div>
        <p className="font-serif text-2xl text-[#1F3E42] group-hover:text-[#006A71] transition-colors">{title}</p>
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#4A6165] mt-1">{subtitle}</p>
      </NavLink>
    </motion.div>
  );
}
